import { Box, Typography, useTheme, Button } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { useState } from "react";
import moment from "moment";

import AssignmentIcon from "@mui/icons-material/Assignment";
import AddIcon from "@mui/icons-material/Add";
import VisibilityIcon from "@mui/icons-material/Visibility";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

import ReportTable, { RRow, RCell } from "components/Tables/ReportTable";
import ListPageHeader from "components/list/ListPageHeader";
import ListFilterBar from "components/list/ListFilterBar";
import ListDateFilterBar from "components/list/ListDateFilterBar";
import ListSearchField from "components/list/ListSearchField";
import FixedSizeDialog from "components/fixedSizeDialog";
import PurchaseRequisitionViewForm from "../forms/purchaseRequisitionForms/purchaseRequisitionViewForm";
import { usePurchaseRequisitionList } from "api/hooks/inventory";
import { requester } from "api/pesabackend";
import useListDateFilter from "hooks/useListDateFilter";

export default function PurchaseRequisition({ setIsSuccess }) {
  const navigate = useNavigate();
  const theme    = useTheme();
  const isDark   = theme.palette.mode === "dark";

  const bodyBg = theme.palette.background.alt;
  const accent = theme.palette.secondary.main;
  const textPri = theme.palette.neutral.dark;
  const textSec = theme.palette.neutral.main;

  const business_branch_id = useSelector((s) => s.authReducer.business_branch_id);
  const {
    purchaseRequisitionList, count, isLoadingPurchaseRequisitionList, isLoadingMore, hasMore, loadMore,
    dateFilter, setDateFilter, customRange, setCustomRange, applyCustomRange,
    setSearch, setNewPurchaseRequisitionData,
  } = usePurchaseRequisitionList(1, business_branch_id);

  const { displayDateFilter, handleFilterChange, handleCustomApply } = useListDateFilter({
    dateFilter, setDateFilter, applyCustomRange, customRange,
  });

  const [openView, setOpenView] = useState(false);
  const [openDelete, setOpenDelete] = useState(false);
  const [viewData, setViewData] = useState({});
  const [clickedItem, setClickedItem] = useState("");

  const handleClickView = async (id) => {
    const res = await requester.get(`/inventory/purchase_requisition/get_form/${id}`);
    if (res.status === 200) { setClickedItem(id); setViewData(res.data.data); setOpenView(true); }
  };

  const handleClickEditOpen = async (id) => {
    const res = await requester.get(`/inventory/purchase_requisition/get/${id}`);
    if (res.status === 200) navigate("/editPurchaseRequisitionForm", { state: res.data.data });
  };

  const handleClickDeleteOpen = async (id) => {
    const res = await requester.get(`/inventory/purchase_requisition/get/${id}`);
    if (res.status === 200) { setClickedItem(id); setOpenDelete(true); }
  };

  const handleClickDelete = async () => {
    const res = await requester.post(`/inventory/purchase_requisition/delete/${clickedItem}`);
    if (res.status === 201) { setIsSuccess(true); setNewPurchaseRequisitionData(true); setOpenDelete(false); }
  };

  const handleClickApprove = async () => {
    const res = await requester.post(`/inventory/purchase_requisition/approve/${clickedItem}`);
    if (res.status === 201) { setIsSuccess(true); setNewPurchaseRequisitionData(true); setOpenView(false); }
  };

  /* status badge */
  const StatusBadge = ({ approved }) => (
    <Box sx={{
      display: "inline-flex", alignItems: "center", gap: "5px",
      px: "8px", py: "3px", borderRadius: "20px",
      backgroundColor: approved ? "rgba(29,158,117,0.12)" : "rgba(186,117,23,0.1)",
    }}>
      <Box sx={{ width: "5px", height: "5px", borderRadius: "50%", backgroundColor: approved ? accent : "#BA7517", flexShrink: 0 }} />
      <Typography sx={{ fontSize: "11px", fontWeight: 600, color: approved ? accent : "#BA7517", lineHeight: 1 }}>
        {approved ? "Approved" : "Pending"}
      </Typography>
    </Box>
  );

  const columns = [
    { label: "#",              width: 40  },
    { label: "Date Added"                 },
    { label: "Req. No."                   },
    { label: "Req. Date"                  },
    { label: "Created By"                 },
    { label: "Approved By"                },
    { label: "Status"                     },
    { label: "Actions",       align: "center" },
  ];

  return (
    <Box sx={{ minHeight: "100vh", backgroundColor: bodyBg, p: "1.5rem 2rem", display: "flex", flexDirection: "column", gap: "1.5rem" }}>

      <ListPageHeader icon={<AssignmentIcon sx={{ fontSize: "22px" }} />} title="Purchase Requisitions" count={count}>
        <Button onClick={() => navigate("/purchaseRequisitionForm")} startIcon={<AddIcon sx={{ fontSize: "16px" }} />}
          sx={{ fontSize: "13px", fontWeight: 500, px: "1rem", py: "0.45rem", borderRadius: "8px", backgroundColor: accent, color: "#fff", textTransform: "none", "&:hover": { backgroundColor: "#0F6E56" } }}>
          Add Requisition
        </Button>
      </ListPageHeader>

      <ListFilterBar>
        <ListDateFilterBar embedded dateFilter={displayDateFilter} customRange={customRange}
          onFilterChange={handleFilterChange} onCustomRangeChange={setCustomRange} onCustomApply={handleCustomApply} />
        <ListSearchField placeholder="Search requisition…" onSearch={setSearch} />
      </ListFilterBar>

      <ReportTable columns={columns} rows={purchaseRequisitionList} loading={isLoadingPurchaseRequisitionList}
        hasMore={hasMore} onLoadMore={loadMore} loadingMore={isLoadingMore}
        emptyMessage="No requisitions found"
        renderRow={(row, index) => (
          <RRow key={row.id} index={index}>
            <RCell>{index + 1}</RCell>
            <RCell>{moment(row.date_added).format("LL")}</RCell>
            <RCell><Typography sx={{ fontSize: "13px", fontWeight: 500, color: textPri }}>{row.id}</Typography></RCell>
            <RCell>{row.requisition_date}</RCell>
            <RCell>{row.added_by.first_name} {row.added_by.last_name}</RCell>
            <RCell>{row.approved_by ? `${row.approved_by.first_name} ${row.approved_by.last_name}` : <Typography sx={{ fontSize: "12px", color: textSec }}>—</Typography>}</RCell>
            <RCell><StatusBadge approved={row.status !== 0} /></RCell>
            <RCell align="center">
              <Box sx={{ display: "flex", gap: "5px", justifyContent: "center" }}>
                <Box onClick={() => handleClickView(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(29,158,117,0.3)", cursor: "pointer", color: accent, fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(29,158,117,0.08)" } }}>
                  {row.status === 0 ? <><CheckCircleIcon sx={{ fontSize: "14px" }} /> View & Approve</> : <><VisibilityIcon sx={{ fontSize: "14px" }} /> View</>}
                </Box>
                {row.status === 0 && (
                  <>
                    <Box onClick={() => handleClickEditOpen(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(186,117,23,0.3)", cursor: "pointer", color: "#BA7517", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(186,117,23,0.08)" } }}>
                      <EditOutlinedIcon sx={{ fontSize: "14px" }} /> Edit
                    </Box>
                    <Box onClick={() => handleClickDeleteOpen(row.id)} sx={{ display: "inline-flex", alignItems: "center", gap: "4px", px: "8px", py: "4px", borderRadius: "6px", border: "1px solid rgba(226,75,74,0.3)", cursor: "pointer", color: "#e24b4a", fontSize: "12px", fontWeight: 500, "&:hover": { backgroundColor: "rgba(226,75,74,0.08)" } }}>
                      <DeleteOutlineIcon sx={{ fontSize: "14px" }} /> Delete
                    </Box>
                  </>
                )}
              </Box>
            </RCell>
          </RRow>
        )}
      />

      <FixedSizeDialog open={openView} handleClose={() => setOpenView(false)} dialogHeading="Purchase Requisition" negativeResponse="Close" maxWidth="lg" scroll="body"
        content={<PurchaseRequisitionViewForm viewData={viewData} handleClickApprove={handleClickApprove} />}
      />
      <FixedSizeDialog open={openDelete} handleClose={() => setOpenDelete(false)} dialogHeading="Delete requisition?" negativeResponse="Cancel"
        positiveResponse="Yes, delete" handleSubmit={handleClickDelete} maxWidth="sm"
        content={
          <Box sx={{ display: "flex", alignItems: "flex-start", gap: "10px", backgroundColor: "rgba(226,75,74,0.07)", border: "1px solid rgba(226,75,74,0.2)", borderRadius: "8px", p: "0.875rem 1rem" }}>
            <DeleteOutlineIcon sx={{ fontSize: "18px", color: "#e24b4a", flexShrink: 0 }} />
            <Typography sx={{ fontSize: "13px", color: isDark ? "#f09595" : "#a32d2d", lineHeight: 1.6 }}>This will permanently delete the purchase requisition. This action cannot be undone.</Typography>
          </Box>
        }
      />
    </Box>
  );
}